import React, { useEffect, useState, useRef } from "react";
import backarrow from "../../Assets/svg/backarrow.svg";
import dt from "../../Assets/svg/delete.svg";
import { Link } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Toast, ToastContainer } from "react-bootstrap";
import MultiSelectDropDown from "../common/searchbox/multi-select-dropdown.js";
import SearchWithAPI from "../common/searchbox/searchbox.js";
import { apiCall } from "../../services/api-service.js";

const Projectadd = ({ children }) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};
  const [validations, setValidations] = useState({
    code: "",
    name: "",
    admin: "",
    manager: "",
    users: "",
    components: "",
  });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);
  const [projectCode, setProjectCode] = useState("");
  const [projectName, setProjectName] = useState("");
  const [selectedAdminOption, setAdminOption] = useState(null);
  const [selectedManagerOption, setManagerOption] = useState(null);
  const handleAdminDropdownChange = (option) => {
    setAdminOption(option);
  };
  const handleManagerDropdownChange = (option) => {
    setManagerOption(option);
  };

  const [selectedComponentOptions, setSelectedComponentOptions] = useState([]);
  const handleComponentDropdownChange = (selectedOptions) => {
    if (!selectedOptions) return;
    const optVal = String(selectedOptions.value ?? selectedOptions.id ?? "");
    if (!optVal) return;

    setSelectedComponentOptions((prev) => {
      const exists = prev.some((o) => String(o?.value ?? o?.id ?? "") === optVal);
      return exists
        ? prev.filter((o) => String(o?.value ?? o?.id ?? "") !== optVal)
        : [...prev, selectedOptions];
    });
  };

  // Team (multi-select of teams) drives Project Members: selecting teams loads
  // their members via get_team_members. Members are also individually removable.
  const [selectedTeamOptions, setSelectedTeamOptions] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  // Members added individually via the "Select Members" picker. Kept separate so
  // they survive team changes (which re-derive the team-based members).
  const [manualMembers, setManualMembers] = useState([]);
  // Member values explicitly removed by the user, so re-deriving from teams does
  // not bring them back.
  const [excludedMemberIds, setExcludedMemberIds] = useState([]);
  // On edit-load the members come from the edit API; skip the next team-driven
  // fetch so we don't overwrite the saved members with the teams' current ones.
  const skipNextTeamFetch = useRef(false);

  const handleTeamDropdownChange = (option) => {
    if (!option) return;
    const optVal = String(option.value ?? option.id ?? option.uuid ?? option.team_id ?? "");
    if (!optVal) return;

    setSelectedTeamOptions((teams) => {
      const exists = teams.some(
        (t) => String(t?.value ?? t?.id ?? t?.uuid ?? t?.team_id ?? "") === optVal
      );
      return exists
        ? teams.filter((t) => String(t?.value ?? t?.id ?? t?.uuid ?? t?.team_id ?? "") !== optVal)
        : [...teams, option];
    });
  };

  const removeMember = (value) => {
    setProjectMembers((members) => members.filter((m) => m.value !== value));
    setManualMembers((members) => members.filter((m) => m.value !== value));
    setExcludedMemberIds((ids) => (ids.includes(value) ? ids : [...ids, value]));
  };

  // Add an individual employee directly. The team effect (which depends on
  // manualMembers) rebuilds the displayed list, merging team + manual members.
  const addMember = (option) => {
    if (option == null || option.value == null) return;
    // Re-adding a previously removed member un-excludes it.
    setExcludedMemberIds((ids) => ids.filter((v) => v !== option.value));
    setManualMembers((members) =>
      members.some((m) => m.value === option.value) ? members : [...members, option]
    );
  };

  // Fetch members whenever the selected teams change, then merge team-derived
  // members with any individually-added (manual) members — unique by value.
  useEffect(() => {
    // After an edit load, the members are already set from the edit API — don't
    // refetch the teams' current members and overwrite them.
    if (skipNextTeamFetch.current) {
      skipNextTeamFetch.current = false;
      return;
    }
    const teamIds = selectedTeamOptions.map((t) => t.value);
    let cancelled = false;
    (async () => {
      const results = teamIds.length
        ? await Promise.all(
            teamIds.map((teamId) =>
              apiCall("get", "get_employee", null, { params: { team_id: teamId } })
            )
          )
        : [];
      if (cancelled) return;
      const merged = [];
      const seen = new Set();
      const excluded = new Set(excludedMemberIds);
      const pushUnique = (m) => {
        if (m?.value != null && !seen.has(m.value) && !excluded.has(m.value)) {
          seen.add(m.value);
          merged.push(m);
        }
      };
      results.forEach((res) => (res?.data?.suggestionlist ?? []).forEach(pushUnique));
      manualMembers.forEach(pushUnique);
      setProjectMembers(merged);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamOptions, manualMembers, excludedMemberIds]);

  // Client Details: each row has a single client and a multi-select of
  // in-charge employees. Starts with one empty row.
  const [clientRows, setClientRows] = useState([{ client: null, incharge: [] }]);

  const handleRowClientChange = (option, index) => {
    // Don't allow the same client to be selected in more than one row.
    const isDuplicate =
      option != null &&
      clientRows.some((row, i) => i !== index && row.client?.value === option.value);
    if (isDuplicate) {
      setSnackbarMessage("This client is already added.");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }
    setClientRows((rows) => rows.map((row, i) => (i === index ? { ...row, client: option } : row)));
  };

  const handleRowInchargeChange = (option, index) => {
    setClientRows((rows) =>
      rows.map((row, i) => {
        if (i !== index) return row;
        const exists = row.incharge.some((emp) => emp.value === option.value);
        const incharge = exists
          ? row.incharge.filter((emp) => emp.value !== option.value)
          : [...row.incharge, option];
        return { ...row, incharge };
      })
    );
  };

  const addClientRow = () => {
    setClientRows((rows) => [...rows, { client: null, incharge: [] }]);
  };

  const removeClientRow = (index) => {
    setClientRows((rows) => {
      // Keep at least one row; clear it instead of removing the last one.
      if (rows.length === 1) return [{ client: null, incharge: [] }];
      return rows.filter((_, i) => i !== index);
    });
  };

  const resetValidationState = () => ({
    code: "",
    name: "",
    admin: "",
    manager: "",
    users: "",
    components: "",
  });

  const applyValidationErrors = (errorObj = {}) => {
    const validationTemp = resetValidationState();
    Object.entries(errorObj).forEach(([key, value]) => {
      validationTemp[key] = Array.isArray(value) && value.length > 0 ? value[0] : "";
    });
    setValidations(validationTemp);
  };

  const projectSubmit = async () => {
      setValidations(resetValidationState());
      const requestBody = {
        code: projectCode,
        name: projectName,
        admin: selectedAdminOption != null && selectedAdminOption.value,
        manager: selectedManagerOption != null && selectedManagerOption.value,
        teams: selectedTeamOptions.map((item) => item.value),
        users: projectMembers.map((item) => item.value),
        components:
          selectedComponentOptions != null &&
          selectedComponentOptions.map((item) => item.value),
        clients: clientRows
          .filter((row) => row.client != null)
          .map((row) => ({
            client_id: row.client.value,
            incharges: row.incharge.map((emp) => emp.value),
          })),
      };
      const isNew = id === null || id === undefined || id === -1;
      const response = await apiCall(
        isNew ? "post" : "put",
        isNew ? "project" : `project/${id}`,
        requestBody
      );
      if (response?.status === 200 && response.data?.success === true) {
        navigate(-1);
      } else if (response?.data?.error) {
        applyValidationErrors(response.data.error);
      }
  };
  const projectEdit = async () => {
    const response = await apiCall("get", `project/edit/${id}`);
    if (response?.status === 200 && response.data?.success === true) {
      setProjectCode(response.data.project.code ?? "");
      setProjectName(response.data.project.name ?? "");
      setAdminOption({
        value: response.data.project.admin_value,
        label: response.data.project.admin_label,
      });
      setManagerOption({
        value: response.data.project.manager_value,
        label: response.data.project.manager_label,
      });
      // Members come from the edit API below; tell the team effect to skip its
      // next fetch so it doesn't overwrite them. Only needed when there are teams
      // to load (otherwise selectedTeamOptions stays [] and the effect won't run).
      const editTeams = response.data.project.teams ?? [];
      if (editTeams.length > 0) skipNextTeamFetch.current = true;
      setSelectedTeamOptions(editTeams);
      // Treat saved members as manual so they survive team re-derivation on edit.
      setProjectMembers(response.data.project.users ?? []);
      setManualMembers(response.data.project.users ?? []);
      setSelectedComponentOptions(response.data.project.components ?? []);

      // Client Details rows: each client carries its in-charge employees.
      const editClients = response.data.project.clients ?? [];
      setClientRows(
        editClients.length > 0
          ? editClients.map((c) => ({
              client: { value: c.value, label: c.label },
              incharge: c.incharges ?? [],
            }))
          : [{ client: null, incharge: [] }]
      );
    }
  };

  useEffect(() => {
    if (id != null && id !== undefined) {
      projectEdit();
    }
  }, []);
  return (
    <Container fluid style={{ width: "100%", padding: "0px" }}>
      <div className="d-flex side3">
        <Row
          sm={12}
          style={{
            "--bs-gutter-x": "0px",
            margin: "0px",
            width: "100%",
            // height: "100vh",
            overflow: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            justifyContent: "center",
          }}
        >
          <Col>
            <div style={{ padding: "16px 16px 80px 16px" }}>
              <div
                style={{
                  paddingBottom: "16px",
                  gap: "10px",
                  backgroundColor: "#F3F6F9",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <Link to="/project">
                      <div style={{ borderRadius: "100px" }}>
                        <img src={backarrow} />
                      </div>
                    </Link>
                    <div>
                      <span
                        style={{
                          fontFamily: "Satoshi",
                          fontWeight: "700",
                          fontSize: "16px",
                          lineHeight: "21.6px",
                          color: "#212129",
                        }}
                      >
                        New Project
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div
                      onClick={() => {
                        navigate(-1);
                      }}
                      style={{
                        borderRadius: "100px",
                        border: "0.6px solid #E5E5E5",
                        padding: "10px 28px 10px 28px",
                        backgroundColor: "#FFFFFF",
                        cursor: "pointer",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "Satoshi",
                          fontWeight: "700",
                          fontSize: "14px",
                          lineHeight: "18.9px",
                          color: "#21272C",
                        }}
                      >
                        Cancel
                      </span>
                    </div>

                    <div
                      onClick={() => {
                        projectSubmit();
                      }}
                      style={{
                        borderRadius: "100px",
                        border: "0.6px solid #E5E5E5",
                        padding: "10px 28px 10px 28px",
                        backgroundColor: "#3253EB",
                        cursor: "pointer",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "Satoshi",
                          fontWeight: "700",
                          fontSize: "14px",
                          lineHeight: "18.9px",
                          color: "#FFFFFF",
                        }}
                      >
                        Save
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  borderRadius: "16px",
                  padding: "24px",
                  gap: "24px",
                  backgroundColor: "#FFFFFF",
                }}
              >
                <div style={{ marginBottom: "16px" }}>
                  <p
                    style={{
                      fontFamily: "satoshi",
                      fontWeight: "700",
                      fontSize: "20px",
                      lineHeight: "27px",
                      color: "212129",
                    }}
                  >
                    Add Project
                  </p>
                  <span
                    style={{
                      fontFamily: "satoshi",
                      fontWeight: "400",
                      fontSize: "14px",
                      lineHeight: "18.9px",
                      color: "#8790A1",
                    }}
                  >
                    Enter below details to create new project
                  </span>
                </div>
{/* Project Details + Client Details (side by side) */}
                <Row className="g-3" style={{ marginBottom: "24px" }}>
                  <Col lg={6}>
                <div
                  style={{
                    borderBottom: "1px dashed #E5E5E5",
                  }}
                  className="row"
                >
                  <Row>
                    <Col sm={12} md={6} >
                      <div style={{ marginBottom: "16px" }}>
                        <p
                          style={{
                            fontFamily: "Satoshi",
                            fontWeight: "500",
                            fontSize: "13px",
                            lineHeight: "17.55px",
                            color: "#212129",
                            marginBottom: "8px",
                          }}
                        >
                          Project Code<span style={{ color: "red" }}> *</span>
                        </p>
                        <input
                          type="text"
                          placeholder="Enter here..."
                          value={projectCode}
                          onChange={(event) => {
                            setProjectCode(event.target.value);
                          }}
                          className="col-12"
                          style={{
                            borderRadius: "10px",
                            border: "1.4px solid #E5E5E5",
                            padding: "16px",
                            gap: "77px",
                            height: "44px",
                            backgroundColor: "#FFFFFF",
                            display: "block",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                          }}
                        >
                          {validations.code}
                        </span>
                      </div>
                    </Col>
                    <Col sm={12} md={6} >
                      <div style={{ marginBottom: "16px" }}>
                        <p
                          style={{
                            fontFamily: "Satoshi",
                            fontWeight: "500",
                            fontSize: "13px",
                            lineHeight: "17.55px",
                            color: "#212129",
                            marginBottom: "8px",
                          }}
                        >
                          Project Name<span style={{ color: "red" }}> *</span>
                        </p>
                        <input
                          type="text"
                          placeholder="Enter here..."
                          value={projectName}
                          onChange={(event) => {
                            setProjectName(event.target.value);
                          }}
                          className="col-12"
                          style={{
                            borderRadius: "10px",
                            border: "1.4px solid #E5E5E5",
                            padding: "16px",
                            gap: "77px",
                            height: "44px",
                            backgroundColor: "#FFFFFF",
                            display: "block",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                          }}
                        >
                          {validations.name}
                        </span>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12} md={6} >
                      <div style={{ marginBottom: "16px" }}>
                        <p
                          style={{
                            fontFamily: "Satoshi",
                            fontWeight: "500",
                            fontSize: "13px",
                            lineHeight: "17.55px",
                            color: "#212129",
                            marginBottom: "8px",
                          }}
                        >
                          Project Admin<span style={{ color: "red" }}> *</span>
                        </p>
                        <SearchWithAPI
                          onDropdownChange={handleAdminDropdownChange}
                          apiUrl={apiUrl + "get_employee"}
                          params={{}}
                          placeholder={"Admin"}
                          selectedValue={
                            selectedAdminOption != null
                              ? selectedAdminOption.label
                              : ""
                          }
                          selectedOption={selectedAdminOption}
                        />
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                          }}
                        >
                          {validations.admin}
                        </span>
                      </div>
                    </Col>
                    <Col sm={12} md={6} >
                      <div style={{ marginBottom: "16px" }}>
                        <p
                          style={{
                            fontFamily: "Satoshi",
                            fontWeight: "500",
                            fontSize: "13px",
                            lineHeight: "17.55px",
                            color: "#212129",
                            marginBottom: "8px",
                          }}
                        >
                          Project Manager
                          <span style={{ color: "red" }}> *</span>
                        </p>
                        <SearchWithAPI
                          onDropdownChange={handleManagerDropdownChange}
                          apiUrl={apiUrl + "get_employee"}
                          params={{}}
                          placeholder={"Manager"}
                          selectedValue={
                            selectedManagerOption != null
                              ? selectedManagerOption.label
                              : ""
                          }
                          selectedOption={selectedManagerOption}
                        />
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                          }}
                        >
                          {validations.manager}
                        </span>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12} >
                      <div style={{ marginBottom: "16px" }}>
                        <p
                          style={{
                            fontFamily: "Satoshi",
                            fontWeight: "500",
                            fontSize: "13px",
                            lineHeight: "17.55px",
                            color: "#212129",
                            marginBottom: "8px",
                          }}
                        >
                          Team
                        </p>
                        <MultiSelectDropDown
                          onDropdownChange={handleTeamDropdownChange}
                          apiUrl={"get_team"}
                          placeholder={"Select Team"}
                          selectedOption={selectedTeamOptions}
                          maxVisibleLabels={8}
                        />
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                          }}
                        >
                          {validations.users}
                        </span>
                      </div>
                    </Col>
                  </Row>
                </div>
                  </Col>

                  {/* Client Details */}
                  <Col lg={6}>
                    <div
                      style={{
                        padding: "16px",
                        borderRadius: "16px",
                        backgroundColor: "#F5F5F5",
                        height: "100%",
                      }}
                    >
                      <p
                        style={{
                          fontFamily: "Satoshi",
                          fontWeight: "700",
                          fontSize: "15px",
                          lineHeight: "20.25px",
                          color: "#212129",
                          marginBottom: "16px",
                        }}
                      >
                        Client Details
                      </p>

                      {clientRows.map((row, index) => (
                        <div
                          key={"client-row-" + index}
                          style={{ display: "flex", alignItems: "anchor-center", gap: "8px", marginBottom: "12px" }}
                        >
                          <Row className="g-2" style={{ flex: 1, margin: 0 }}>
                            <Col xs={6} data-testid={"client-name-" + index}>
                              {index === 0 && (
                                <p
                                  style={{
                                    fontFamily: "Satoshi",
                                    fontWeight: "500",
                                    fontSize: "13px",
                                    lineHeight: "17.55px",
                                    color: "#212129",
                                    marginBottom: "8px",
                                  }}
                                >
                                  Client Name
                                </p>
                              )}
                              <SearchWithAPI
                                onDropdownChange={(option) => handleRowClientChange(option, index)}
                                apiUrl={apiUrl + "get_client"}
                                params={{}}
                                placeholder={"Select"}
                                selectedValue={row.client != null ? row.client.label : ""}
                                selectedOption={row.client}
                              />
                            </Col>
                            <Col xs={6}>
                              {index === 0 && (
                                <p
                                  style={{
                                    fontFamily: "Satoshi",
                                    fontWeight: "500",
                                    fontSize: "13px",
                                    lineHeight: "17.55px",
                                    color: "#212129",
                                    marginBottom: "8px",
                                  }}
                                >
                                  In-charge
                                </p>
                              )}
                              <MultiSelectDropDown
                                onDropdownChange={(option) => handleRowInchargeChange(option, index)}
                                apiUrl={apiUrl + "get_employee"}
                                placeholder={"Select"}
                                selectedOption={row.incharge}
                                maxVisibleLabels={2}
                              />
                            </Col>
                          </Row>
                          {/* Delete this client row */}
                          <div
                            data-testid={"remove-client-row-" + index}
                            onClick={() => removeClientRow(index)}
                            title="Remove"
                            style={{
                              flexShrink: 0,
                              marginTop: index === 0 ? "25px" : "0",
                              width: "40px",
                              height: "40px",
                              borderRadius: "10px",
                              border: "1px solid #FF4444",
                              backgroundColor: "#FFF1F1",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              color: "#FF4444",
                              fontSize: "18px",
                              lineHeight: 1,
                            }}
                          >
                            <img
                              src={dt}
                              alt="remove"
                              style={{
                                width: "16px",
                                height: "16px",
                                // Recolor the delete SVG to red (#FF4444).
                                filter:
                                  "invert(36%) sepia(89%) saturate(2000%) hue-rotate(335deg) brightness(100%) contrast(105%)",
                              }}
                            />
                          </div>
                        </div>
                      ))}

                      <div
                        onClick={addClientRow}
                        style={{
                          marginTop: "4px",
                          height: "40px",
                          width: "fit-content",
                          borderRadius: "100px",
                          padding: "8px 20px",
                          border: "1px solid #6929E2",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          cursor: "pointer",
                          fontFamily: "Satoshi",
                          fontWeight: "700",
                          fontSize: "14px",
                          lineHeight: "18.9px",
                          color: "#6929E2",
                        }}
                      >
                        + Add
                      </div>
                    </div>
                  </Col>
                </Row>
{/* Project Members */}
                <div
                  style={{
                    padding: "16px",
                    borderRadius: "16px",
                    backgroundColor: "#F5F5F5",
                    marginBottom: "24px",
                  }}
                >
                  <p
                    style={{
                      fontFamily: "Satoshi",
                      fontWeight: "700",
                      fontSize: "15px",
                      lineHeight: "20.25px",
                      color: "#212129",
                      marginBottom: "12px",
                    }}
                  >
                    Project Members ({projectMembers.length})
                  </p>

                  {/* Pick an employee to add directly as a project member. */}
                  <div style={{ marginBottom: "16px" }}>
                    <SearchWithAPI
                      onDropdownChange={addMember}
                      apiUrl={apiUrl + "get_employee"}
                      params={{}}
                      placeholder={"Select Members"}
                      selectedValue={""}
                      selectedOption={null}
                    />
                  </div>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                    {projectMembers
                      .map((member) => (
                        <div
                          key={member.value}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "8px 12px",
                            borderRadius: "10px",
                            border: "1px solid #E5E5E5",
                            backgroundColor: "#FFFFFF",
                          }}
                        >
                          {member.profile ? (
                            <img
                              src={member.profile}
                              alt={member.label}
                              style={{ width: "28px", height: "28px", borderRadius: "50%", objectFit: "cover" }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "28px",
                                height: "28px",
                                borderRadius: "50%",
                                backgroundColor: "#F0E8FF",
                                color: "#6929E2",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontFamily: "Satoshi",
                                fontWeight: "700",
                                fontSize: "13px",
                                flexShrink: 0,
                              }}
                            >
                              {(member.label ?? "?").charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                            <span
                              style={{
                                fontFamily: "Satoshi",
                                fontWeight: "500",
                                fontSize: "14px",
                                color: "#212129",
                              }}
                            >
                              {member.label}
                            </span>
                            {member.email && (
                              <span
                                style={{
                                  fontFamily: "Satoshi",
                                  fontWeight: "400",
                                  fontSize: "12px",
                                  color: "#8790A1",
                                }}
                              >
                                {member.email}
                              </span>
                            )}
                          </div>
                          <span
                            data-testid={"remove-member-" + member.value}
                            onClick={() => removeMember(member.value)}
                            style={{
                              marginLeft: "8px",
                              cursor: "pointer",
                              color: "#8790A1",
                              fontSize: "16px",
                              lineHeight: 1,
                            }}
                          >
                            ×
                          </span>
                        </div>
                      ))}
                  </div>
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 400,
                      color: "#FF4444",
                    }}
                  >
                    {validations.users}
                  </span>
                </div>
{/* Components */}
                <div
                  style={{
                    gap: "16px",
                    padding: "16px",
                    borderBottom: "1px",
                    borderRadius: "16px",
                    backgroundColor: "#F5F5F5",
                  }}
                >
                  <p
                    style={{
                      fontFamily: "Satoshi",
                      fontWeight: "700",
                      fontSize: "15px",
                      lineHeight: "20.25px",
                      color: "#212129",
                    }}
                  >
                    Components
                  </p>
                  <MultiSelectDropDown
                    onDropdownChange={handleComponentDropdownChange}
                    apiUrl={apiUrl + `get_parent_component`}
                    placeholder={"Component"}
                    selectedOption={selectedComponentOptions}
                  />
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 400,
                      color: "#FF4444",
                    }}
                  >
                    {validations.components}
                  </span>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
      <ToastContainer position="top-end">
        <Toast
          className="d-inline-block m-1"
          bg={snackbarStatus === true ? "success" : "danger"}
          show={showSnackbar}
          onClose={handleSnackbarClose}
          delay={3000}
          autohide
        >
          {/*  <Toast.Header>
            <strong className="mr-auto">Snackbar Title</strong>
          </Toast.Header> */}
          <Toast.Body style={{ color: "#ffffff" }}>
            {snackbarMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </Container>
  );
};

export default Projectadd;
